import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from '../Footer';

/* ── Grid background ── */
const GridBg = () => (
  <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
    <div
      className="absolute inset-0 opacity-[0.03]"
      style={{
        backgroundImage: `linear-gradient(#54E346 1px, transparent 1px), linear-gradient(90deg, #54E346 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
  </div>
);

/* ── Accordion section ── */
const AccordionSection = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-dark-border/40 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left bg-dark-card/50 hover:bg-dark-card/80 transition-colors"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-white">{title}</span>
        <motion.svg
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          className="w-5 h-5 text-primary flex-shrink-0"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </motion.svg>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-6 py-5 text-gray-400 text-sm leading-relaxed space-y-3">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Main layout ── */
const LegalPageLayout = ({ title, subtitle, lastUpdated, children }) => (
  <div className="bg-dark text-white min-h-screen relative">
    <GridBg />

    {/* Header */}
    <header className="relative z-10 border-b border-dark-border/30">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#08CB00] hover:text-[#54E346] transition-colors text-sm font-medium"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>
        <Link to="/" aria-label="OpticVance home">
          <img
            src="/logo.svg"
            alt="OpticVance logo"
            className="w-32 h-32 sm:w-36 sm:h-36"
            style={{ display: 'block', filter: 'drop-shadow(0 0 36px rgba(8,203,0,0.55))' }}
          />
        </Link>
      </div>
    </header>

    {/* Hero */}
    <motion.section
      className="relative z-10 py-16 sm:py-24"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/20 bg-primary/5 mb-6"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
          <span className="text-xs text-primary font-mono uppercase tracking-wider">Legal</span>
        </motion.div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
          {title}
        </h1>
        {subtitle && <p className="text-gray-400 text-lg max-w-2xl mx-auto">{subtitle}</p>}
        {lastUpdated && (
          <p className="text-gray-500 text-sm mt-4">Last updated: {lastUpdated}</p>
        )}
      </div>
    </motion.section>

    {/* Content */}
    <section className="relative z-10 pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {children}
        </motion.div>
      </div>
    </section>

    <Footer />
  </div>
);

export { AccordionSection };
export default LegalPageLayout;
